<script>
import {defineComponent} from 'vue'
import TicketLayouts from "@/components/Tickets/layouts/TicketLayouts.vue";
import {processObjectNullValues} from "@/common/processObjectNullValues";
import {formatIsoToCustom} from "@/common/formatToIso"
import api from "@/api/api"

export default defineComponent({
  name: "TicketStatuses",
  components: {TicketLayouts},
  data() {
    return {
      statusParams: {
        slug: "",
        name: "",
        enabled: true
      },
      search: '',
      ticketStatuses: [],
      isSidebarLoading: false,
      sidebarFields: [],
      isLoading: false,
      selectedStatus: {},
      fields: [
        {key: 'id', label: '№', sortable: true},
        {key: 'date', label: 'Дата', sortable: true},
        {key: 'name', label: 'Название'},
        {key: 'enabled', label: 'Включено',},
        {key: "actions", label: "Действия"}
      ]
    }
  },
  computed: {
    rows() {
      const query = this.search.trim().toLowerCase()
      if (!query) return this.ticketStatuses || []
      return (this.ticketStatuses || []).filter(item =>
          [item.id, item.name, item.slug].some(field => String(field ?? '').toLowerCase().includes(query)))
    },
  },
  methods: {
    async getTicketStatuses() {
      this.isLoading = true;
      try {
        const res = await api.tickets.getListActionStatuses()
        this.ticketStatuses = res.data?.data?.map((status) => processObjectNullValues({
          id: status.id,
          slug: status.slug,
          name: status.name,
          enabled: status.enabled ? "Да" : "Нет",
          date: formatIsoToCustom(status.created_at),
          _original: status,
        }))
        this.isLoading = false;

      } catch (e) {
        console.log(e)
        this.$bvToast.toast(e.response.message || e.response.data.message || "Произошла ошибка", {
          title: "Ошибка сервера",
          variant: "danger",
        })
      } finally {
        this.isLoading = false;
      }
    },
    async createStatus() {
      if (this.statusParams.name === "") {
        this.$bvToast.toast('Введите название', {
          title: "Валидация",
          variant: "danger",
        })
        return false
      }
      if(this.statusParams.slug === "") {
        this.$bvToast.toast('Введите слоган', {
          title: "Валидация",
          variant: "danger",
        })
        return  false
      }
      try {
        const res = await api.tickets.createActionStatus(this.statusParams)
        const newStatus = res.data?.data
        const formattedStatus = processObjectNullValues({
          id: newStatus.id,
          name: newStatus.name,
          date: formatIsoToCustom(newStatus.created_at),
          username: newStatus.created_by?.username || newStatus.created_by?.email,
          enabled: newStatus.enabled ? 'Да' : 'Нет',
          _original: newStatus
        })
        this.ticketStatuses.push(formattedStatus)
        this.$bvModal.hide('create-status')
        this.statusParams.slug = ""
        this.statusParams.name = ""
      }catch (e) {
        console.log(e)
        this.$bvToast.toast(e.response.message || e.response.data.message || 'Произошла ошибка', {
          title: "Ошибка сервера",
          variant: "danger",
        })
      }
    },
    async deleteStatus(status) {
      try {
        if (status && status.id) {
          await api.tickets.deleteActionStatus(status.id)
        }
        this.ticketStatuses = this.ticketStatuses.filter(item => item.id !== status.id)
      } catch (e) {
        console.log(e)
        this.$bvToast.toast(e.response.message || e.response.data.message || 'Произошла ошибка', {
          title: "Ошибка сервера",
          variant: "danger",
        })
      }
    },
    async openEditSidebar(item) {
      this.sidebarFields = [
        {
          key: 'name',
          label: 'Название',
          type: 'text',
          placeholder: 'Введите название',
          required: true
        },
        {
          key: 'enabled',
          label: 'Включено',
          type: 'checkbox',
          default: true
        }
      ]
      this.selectedStatus = {
        id: item.id,
        name: item.name,
        enabled: item.enabled
      }
      this.$root.$emit('bv::toggle::collapse', 'sidebar-right')
    },
    async handleSaveStatus(formData) {
      this.isSidebarLoading = true
      try {
        const res = await api.tickets.updateActionStatus(this.selectedStatus.id, {
          name: formData.name,
          enabled: formData.enabled === "Да"
        })
        const updatedStatus = res.data?.data || formData
        const index = this.ticketStatuses?.findIndex(type => type.id === formData.id)
        if (index !== -1) {
          const oldItem = this.ticketStatuses[index]
          this.$set(this.ticketStatuses, index, processObjectNullValues({
            id: updatedStatus.id,
            date: oldItem.date,
            name: updatedStatus.name,
            enabled: updatedStatus.enabled ? "Да" : "Нет",
            username: oldItem.username,
            slug: oldItem.slug,
            _original: updatedStatus
          }))
        }
        this.$bvToast.toast('Статус успешно обновлен', {
          title: "Успех",
          variant: "success",
        })
        this.$root.$emit('bv::toggle::collapse', 'sidebar-right')
        this.selectedStatus = null
      } catch (e) {
        console.log(e)
        this.$bvToast.toast(e.response.data.message ||e.response.message || 'Произошла ошибка', {
          title: "Ошибка сервера",
          variant: "success",
        })
      } finally {
        this.isSidebarLoading = false;
      }

    }
  },
  mounted() {
    this.getTicketStatuses()
  }
})
</script>

<template>
  <TicketLayouts
      @sidebar-save="handleSaveStatus"
      :sidebar-item="selectedStatus"
      :sidebar-fields="sidebarFields"
      :sidebar-loading="isSidebarLoading"
      title="Статусы обращений"
  >
    <div class="tr">
      <Breadcrumbs main="Тикеты" title="Статусы обращений"/>

      <div class="container-fluid tr-wrap">
        <div class="tr-toolbar">
          <div class="tr-search">
            <i class="fa fa-search tr-search__icon"></i>
            <input type="search" class="tr-search__input" v-model="search"
                   placeholder="ID, название или слаг…" @keydown.esc.prevent="search = ''"/>
            <button v-if="search" type="button" class="tr-search__clear" @click="search = ''">
              <i class="fa fa-times"></i>
            </button>
          </div>

          <span class="tr-spacer"></span>

          <button type="button" class="tr-icon-btn" :class="{ 'is-spin': isLoading }"
                  @click="getTicketStatuses" v-b-tooltip.hover.bottom="'Обновить'">
            <i class="fa fa-refresh"></i>
          </button>

          <button type="button" class="tr-btn-primary" v-b-modal.create-status>
            <i class="fa fa-plus"></i> Создать статус
          </button>
        </div>

        <div class="tr-panel">
          <div v-if="isLoading" class="tr-skeleton">
            <div v-for="n in 5" :key="'sk' + n" class="tr-skeleton__row">
              <span class="tr-sk"></span><span class="tr-sk"></span>
              <span class="tr-sk"></span><span class="tr-sk"></span><span class="tr-sk"></span>
            </div>
          </div>

          <div v-else-if="!rows.length" class="tr-empty">
            <div class="tr-empty__art"><i class="fa fa-flag-o"></i></div>
            <div class="tr-empty__title">{{ search ? 'Ничего не нашлось' : 'Статусов нет' }}</div>
            <div class="tr-empty__text">
              {{ search ? 'Попробуйте изменить запрос.' : 'Из статусов собираются колонки доски тикетов.' }}
            </div>
            <button type="button" class="tr-btn-primary" v-b-modal.create-status>
              <i class="fa fa-plus"></i> Создать статус
            </button>
          </div>

          <div v-else class="tr-table-wrap">
            <table class="tr-table">
              <thead>
              <tr>
                <th>ID</th>
                <th>Название</th>
                <th>Слаг</th>
                <th>Включён</th>
                <th>Создан</th>
                <th></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="item in rows" :key="'st' + item.id">
                <td><span class="tr-id">#{{ item.id }}</span></td>
                <td><span class="tr-name">{{ item.name }}</span></td>
                <td><span class="tr-mono">{{ item.slug || '—' }}</span></td>
                <td>
                  <span class="tr-badge" :class="item.enabled === 'Да' ? 'tr-badge--ok' : 'tr-badge--off'">
                    {{ item.enabled === 'Да' ? 'Включён' : 'Выключен' }}
                  </span>
                </td>
                <td class="tr-num">{{ item.date }}</td>
                <td>
                  <div class="tr-actions">
                    <button type="button" class="tr-act" @click="openEditSidebar(item)"
                            v-b-tooltip.hover.top="'Редактировать'">
                      <i class="fa fa-pencil"></i>
                    </button>
                    <button type="button" class="tr-act tr-act--danger" @click="deleteStatus(item)"
                            v-b-tooltip.hover.top="'Удалить'">
                      <i class="fa fa-trash-o"></i>
                    </button>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <b-modal id="create-status" title="Новый статус" hide-footer>
      <b-form @submit.prevent="createStatus">
        <label class="font-weight-bold">Название</label>
        <b-form-input v-model="statusParams.name" placeholder="Например: В работе"/>
        <label class="font-weight-bold mt-3">Слаг</label>
        <b-form-input v-model="statusParams.slug" placeholder="in_progress"/>
        <b-form-checkbox class="mt-3" v-model="statusParams.enabled">Включён</b-form-checkbox>
        <b-button class="mt-3 w-100" variant="primary" type="submit">Создать</b-button>
      </b-form>
    </b-modal>
  </TicketLayouts>
</template>

<style scoped lang="scss">
@import "~@/assets/scss/ticket-ref.scss";
</style>
